"use client";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search, Plus, Edit3, Trash2, Grid3X3, Tag, Table, Layers,
  CheckCircle, XCircle, LayoutList, Package, RefreshCcw, X,
} from 'lucide-react';
import SubCategoryModal from './SubCategoryModal';
import Pagination, { usePagination } from '../shared/Pagination';
import { useSubCategories } from '../context/SubcategoryContext';
import { useCategories }    from '../context/CategoryContext';
import { useProducts }      from '../context/ProductsContext';
import { useLocation } from "react-router-dom";
const SubCategoryPage = () => {
  const { subCategories, loading, fetchSubCategories, addSubCategory, updateSubCategory, deleteSubCategory, toggleStatus } = useSubCategories();
  const { categories, fetchCategories } = useCategories();
  const { products = [] } = useProducts();

  // ── Product count per sub-category ─────────────────────────────────────────
  const productCountMap = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const id = String(p.subCategoryId || p.subcategory_id || p.sub_category_id || "");
      if (id) map[id] = (map[id] || 0) + 1;
    });
    return map;
  }, [products]);

  // ── State ───────────────────────────────────────────────────────────────────
 const location = useLocation();
const [searchTerm, setSearchTerm] = useState(location.state?.prefillSearch || "");
  const [statusFilter,    setStatusFilter]     = useState('all');
  const [categoryFilter,  setCategoryFilter]   = useState('all');
  const [sortBy,          setSortBy]           = useState('name');
  const [viewMode,        setViewMode]         = useState('grid');
  const [showAddModal,    setShowAddModal]      = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [toast, setToast] = useState({ visible:false, message:'', type:'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ visible:true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible:false })), 3000);
  }, []);

  useEffect(() => {
    fetchSubCategories().catch(e => console.warn(e));
    fetchCategories().catch(e => console.warn(e));
  }, [fetchSubCategories, fetchCategories]);

  // ── Category status helpers ─────────────────────────────────────────────────
  const categoryStatusMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.status; });
    return map;
  }, [categories]);

  const isCategoryInactive = sub => categoryStatusMap[sub.category_id] !== 'Active';
  const getEffectiveStatus = sub => isCategoryInactive(sub) ? 'Inactive' : sub.status;

  // ── KPIs ────────────────────────────────────────────────────────────────────
  const totalCount    = subCategories.length;
  const activeCount   = subCategories.filter(s => getEffectiveStatus(s) === 'Active').length;
  const inactiveCount = totalCount - activeCount;

  // ── Filter options ──────────────────────────────────────────────────────────
  const categoryOptions = useMemo(() => {
    const opts = categories
      .map(c => ({ value: c.id, label: c.name, count: subCategories.filter(s => String(s.category_id) === String(c.id)).length }))
      .filter(o => o.count > 0);
    return [{ value:'all', label:'All Categories', count:totalCount }, ...opts];
  }, [categories, subCategories, totalCount]);

  useEffect(() => {
    if (categoryFilter === 'all') return;
    if (!categoryOptions.some(o => String(o.value) === String(categoryFilter))) setCategoryFilter('all');
  }, [categoryOptions, categoryFilter]);

  // ── Filtered + sorted ───────────────────────────────────────────────────────
  const filteredSubCategories = useMemo(() => {
    const parseDate = v => { const t = new Date((v||'').replace(' ','T')).getTime(); return isNaN(t) ? 0 : t; };
    return subCategories
      .filter(s => {
        const matchSearch = !searchTerm ||
          s.subcat_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.category_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus   = statusFilter === 'all' || getEffectiveStatus(s) === statusFilter;
        const matchCategory = categoryFilter === 'all' || String(s.category_id) === String(categoryFilter);
        return matchSearch && matchStatus && matchCategory;
      })
      .sort((a, b) => {
        if (sortBy === 'name')       return (a.subcat_name||'').localeCompare(b.subcat_name||'');
        if (sortBy === 'name-desc')  return (b.subcat_name||'').localeCompare(a.subcat_name||'');
        if (sortBy === 'category')   return (a.category_name||'').localeCompare(b.category_name||'');
        if (sortBy === 'newest')     return parseDate(b.create_audit_time) - parseDate(a.create_audit_time);
        if (sortBy === 'oldest')     return parseDate(a.create_audit_time) - parseDate(b.create_audit_time);
        return 0;
      });
  }, [subCategories, searchTerm, statusFilter, categoryFilter, sortBy, categoryStatusMap]);

  const { page:scPage, pageSize:scSize, setPage:setScPage, setPageSize:setScSize,
    totalPages:scTotalPages, totalItems:scTotal, paginated:paginatedSubCats }
    = usePagination(filteredSubCategories, 10);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleStatus(id, currentStatus);
      showToast(`Status updated to ${currentStatus === 'Active' ? 'Inactive' : 'Active'} successfully!`);
    } catch { showToast('Failed to update status.', 'error'); }
  };

  const handleDeleteSubCategory = async id => {
    if (!window.confirm('Are you sure you want to delete this sub-category?')) return;
    try { await deleteSubCategory(id); } catch (e) { console.error(e); }
  };

  const handleEditSubCategory  = sub => { setEditingSubCategory(sub);  setShowAddModal(true); };
  const handleAddSubCategory   = ()  => { setEditingSubCategory(null); setShowAddModal(true); };

  const handleSaveSubCategory = async data => {
    try {
      editingSubCategory
        ? await updateSubCategory(editingSubCategory.id, data)
        : await addSubCategory(data);
    } catch (e) { console.error(e); }
    finally { setEditingSubCategory(null); setShowAddModal(false); }
  };

  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setCategoryFilter('all'); setSortBy('name'); };
  const hasFilters = searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || sortBy !== 'name';

  // ── Toggle switch ────────────────────────────────────────────────────────────
  const ToggleSwitch = ({ subCategoryId, status, disabled = false }) => (
    <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      title={disabled ? 'Category is inactive' : status === 'Active' ? 'Set Inactive' : 'Set Active'}>
      <input type="checkbox" checked={status === 'Active'}
        onChange={() => !disabled && handleToggleStatus(subCategoryId, status)}
        disabled={disabled} className="sr-only peer" />
      <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300 shadow-sm" />
      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5" />
    </label>
  );

  // ── Grid Card ────────────────────────────────────────────────────────────────
  const SubCategoryCard = ({ subCategory }) => {
    const catInactive = isCategoryInactive(subCategory);
    const isActive    = subCategory.status === 'Active' || subCategory.status === '1' || subCategory.status === 1;
    const isGrayed    = catInactive || !isActive;
    const count       = productCountMap[String(subCategory.id)] || 0;
    const desc        = subCategory.description || '';
    const isLong      = desc.length > 80;
    const [expanded, setExpanded] = useState(false);

    return (
      <div className={`bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex flex-col transition-all duration-300 group
        ${isGrayed ? 'opacity-55 grayscale' : 'hover:shadow-lg hover:shadow-teal-100/60 hover:-translate-y-0.5'}`}>

        {/* Image */}
        <div className="relative w-full h-44 overflow-hidden bg-gray-100 flex-shrink-0">
          <img src={subCategory.image} alt={subCategory.subcat_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={e => { e.target.src = 'https://placehold.co/400x180?text=No+Image'; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
         <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
  <h3 className="text-white font-bold text-[15px] leading-snug drop-shadow line-clamp-1 break-all">
    {subCategory.subcat_name}
  </h3>
</div>
          {catInactive && (
            <div className="absolute top-3 left-3">
              <span className="px-2 py-1 bg-gray-800/80 text-white text-[10px] font-semibold rounded-full backdrop-blur-sm">
                Category Inactive
              </span>
            </div>
          )}
          {count > 0 && !catInactive && (
            <div className="absolute top-3 left-3">
              <span className="flex items-center gap-1 px-2 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold rounded-full">
                <Package className="w-2.5 h-2.5" />{count}
              </span>
            </div>
          )}
          <div className="absolute top-3 right-3">
            <ToggleSwitch subCategoryId={subCategory.id} status={subCategory.status} disabled={catInactive} />
          </div>
        </div>

        {/* Body */}
        <div className="p-4 flex flex-col gap-2.5 flex-1">
          {/* Parent category */}
          <div className="flex items-center gap-1.5">
  <Tag className="w-3 h-3 text-teal-500 flex-shrink-0" />
  <span className="text-xs text-teal-600 font-medium truncate break-all">{subCategory.category_name || '—'}</span>
</div>

          {/* Description */}
          <div className="flex-1">
            {desc ? (
              <>
               <p className={`text-xs text-gray-500 leading-relaxed break-all ${expanded ? '' : 'line-clamp-2'}`}>{desc}</p>
                {isLong && (
                  <button onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
                    className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 mt-0.5 transition-colors">
                    {expanded ? 'Show less ↑' : 'Read more ↓'}
                  </button>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-400 italic">No description provided.</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 mt-auto">
            <span className="text-[11px] text-gray-400 font-medium">
              {count > 0 ? `${count} product${count !== 1 ? 's' : ''}` : 'No products yet'}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => handleEditSubCategory(subCategory)}
                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDeleteSubCategory(subCategory.id)}
                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── List row ─────────────────────────────────────────────────────────────────
  const ListRow = ({ subCategory, idx }) => {
    const catInactive = isCategoryInactive(subCategory);
    const isActive    = subCategory.status === 'Active';
    const isGrayed    = catInactive || !isActive;
    const count       = productCountMap[String(subCategory.id)] || 0;
    return (
      <div className={`flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-4 py-3 transition-all duration-200
        ${isGrayed ? 'opacity-55 grayscale' : 'hover:shadow-sm hover:border-teal-200'}`}>
        <span className="text-xs text-gray-400 w-6 text-center flex-shrink-0">{idx + 1}</span>
        <img src={subCategory.image} alt={subCategory.subcat_name}
          className="w-11 h-11 rounded-xl object-cover border border-gray-100 flex-shrink-0"
          onError={e => { e.target.src = 'https://placehold.co/44x44?text=?'; }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{subCategory.subcat_name}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <Tag className="w-3 h-3 text-teal-500 flex-shrink-0" />
            <span className="text-xs text-teal-600 truncate">{subCategory.category_name || '—'}</span>
          </div>
        </div>
        <span className="text-xs text-gray-400 hidden sm:block flex-shrink-0 w-24 text-center">
          {count > 0 ? `${count} product${count !== 1 ? 's' : ''}` : '—'}
        </span>
        <ToggleSwitch subCategoryId={subCategory.id} status={subCategory.status} disabled={catInactive} />
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => handleEditSubCategory(subCategory)}
            className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => handleDeleteSubCategory(subCategory.id)}
            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label:'Total Sub-Categories', value:totalCount,    icon:LayoutList,  accent:'border-l-teal-500',  color:'text-teal-700',  bg:'bg-teal-50' },
            { label:'Active',               value:activeCount,   icon:CheckCircle, accent:'border-l-green-500', color:'text-green-600', bg:'bg-green-50' },
            { label:'Inactive',             value:inactiveCount, icon:XCircle,     accent:'border-l-red-400',   color:'text-red-500',   bg:'bg-red-50'  },
          ].map(({ label, value, icon:Icon, accent, color, bg }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 leading-tight">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Search & Filter bar ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search sub-categories or categories…"
              className="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition" />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category filter */}
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[150px]">
            {categoryOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label} ({o.count})</option>
            ))}
          </select>

          {/* Status filter */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[130px]">
            <option value="all">All Status ({totalCount})</option>
            <option value="Active">Active ({activeCount})</option>
            <option value="Inactive">Inactive ({inactiveCount})</option>
          </select>

          {/* Sort */}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[130px]">
            <option value="name">Name A → Z</option>
            <option value="name-desc">Name Z → A</option>
            <option value="category">By Category</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>

          {/* Clear */}
          {hasFilters && (
            <button onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg hover:border-red-200 transition-colors">
              <RefreshCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}

          <div className="hidden sm:block w-px h-6 bg-gray-200 flex-shrink-0" />

          {/* View toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
            {[{ mode:'grid', Icon:Grid3X3 }, { mode:'list', Icon:Layers }, { mode:'table', Icon:Table }].map(({ mode, Icon }) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                className={`p-1.5 rounded-md transition-colors ${viewMode === mode ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                title={mode.charAt(0).toUpperCase() + mode.slice(1) + ' view'}>
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Add button */}
          <button onClick={handleAddSubCategory}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Sub-Category</span>
          </button>
        </div>

        {/* ── Results count ── */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Showing <span className="font-semibold text-gray-700">{filteredSubCategories.length}</span> of{' '}
            <span className="font-semibold text-gray-700">{totalCount}</span> sub-categories
          </span>
          {loading && <span className="text-teal-600 font-medium animate-pulse">Refreshing…</span>}
        </div>

        {/* ── Content ── */}
        {filteredSubCategories.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl py-20 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Tag className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-500 mb-1">No sub-categories found</h3>
            <p className="text-sm text-gray-400 mb-5">
              {hasFilters ? 'Try adjusting your search or filters.' : 'Create your first sub-category to get started.'}
            </p>
            {hasFilters ? (
              <button onClick={clearFilters}
                className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                Clear filters
              </button>
            ) : (
              <button onClick={handleAddSubCategory}
                className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors">
                Add Sub-Category
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Grid */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {paginatedSubCats.map(s => <SubCategoryCard key={s.id} subCategory={s} />)}
              </div>
            )}

            {/* List */}
           {/* List */}
{viewMode === 'list' && (
  <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
    <div className="space-y-2 p-3">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 py-3 bg-teal-700 text-white text-xs font-semibold uppercase tracking-wider rounded-xl">
        <span className="w-6 text-teal-200">ID</span>
        <span className="w-11">Image</span>
        <span className="flex-1">Name / Category</span>
        <span className="hidden sm:block w-24 text-center">Products</span>
        <span className="w-12 text-center">Status</span>
        <span className="w-16 text-right">Actions</span>
      </div>
      {paginatedSubCats.map((s, i) => <ListRow key={s.id} subCategory={s} idx={i} />)}
    </div>
    <div className="px-4 border-t border-gray-100">
      <Pagination currentPage={scPage} totalPages={scTotalPages} totalItems={scTotal}
        pageSize={scSize} onPageChange={setScPage} onPageSizeChange={setScSize} />
    </div>
  </div>
)}
            {/* Table */}
            {viewMode === 'table' && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-teal-700 text-white">
                    <tr className="bg-teal-700 text-white">
                      <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider w-10">ID</th>
                      <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider w-14">Image</th>
                      <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Sub-Category</th>
                      <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3.5 text-center text-xs font-semibold uppercase tracking-wider">Products</th>
                      <th className="px-4 py-3.5 text-center text-xs font-semibold uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3.5 text-right text-xs font-semibold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedSubCats.map((s, i) => {
                      const catInactive = isCategoryInactive(s);
                      const count = productCountMap[String(s.id)] || 0;
                      return (
                        <tr key={s.id} className={`transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} ${catInactive ? 'opacity-55 grayscale' : 'hover:bg-teal-50/40'}`}>
                          <td className="px-4 py-3 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-4 py-3">
                            <img src={s.image} alt={s.subcat_name}
                              className="w-10 h-10 rounded-xl object-cover border border-gray-100"
                              onError={e => { e.target.src = 'https://placehold.co/40x40?text=?'; }} />
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-800">{s.subcat_name}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Tag className="w-3 h-3 text-teal-500 flex-shrink-0" />
                              <span className="text-gray-600 text-xs">{s.category_name || '—'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-50 px-2.5 py-0.5 rounded-full">
                              <Package className="w-3 h-3" />{count}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <ToggleSwitch subCategoryId={s.id} status={s.status} disabled={catInactive} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => handleEditSubCategory(s)}
                                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors">
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDeleteSubCategory(s.id)}
                                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-4 border-t border-gray-100">
                  <Pagination currentPage={scPage} totalPages={scTotalPages} totalItems={scTotal}
                    pageSize={scSize} onPageChange={setScPage} onPageSizeChange={setScSize} />
                </div>
              </div>
            )}

            {/* Pagination for grid + list */}
            {viewMode !== 'table' && scTotalPages > 1 && (
              <div className="bg-white border border-gray-200 rounded-xl px-4 shadow-sm">
                <Pagination currentPage={scPage} totalPages={scTotalPages} totalItems={scTotal}
                  pageSize={scSize} onPageChange={setScPage} onPageSizeChange={setScSize} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      <SubCategoryModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingSubCategory(null); }}
        subCategory={editingSubCategory}
        categories={categories}
        onSave={handleSaveSubCategory}
      />

      {/* Toast */}
      <div style={{
        position:'fixed', bottom:24, right:24, zIndex:9999,
        transition:'all 0.35s ease',
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.96)',
        pointerEvents: toast.visible ? 'auto' : 'none',
      }}>
        <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-semibold ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-teal-600 text-white'
        }`}>
          {toast.type === 'error' ? <XCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          {toast.message}
        </div>
      </div>
    </div>
  );
};

export default SubCategoryPage;
